import { createSelector } from 'reselect';
import { configSelector } from 'selectors/common';

import { defaultVisibilitySettings, StudentSectionSettingTypeCodes } from '../../const';
import { IRootState } from '../../reducers';
import { IViewVisibilitySetting } from '../../types';
import { LocalVisibilitySettingsService, normalizeViewSettings, toggleViewSettings } from '../../utils';
import { userFunctionalitySelector } from '../users';
import { adminSettingsSelector } from './adminSettings';
import { adminViewModeSettingsSelector } from './adminViewModeSettings';
import { userSettingsSelector } from './userSettings';
import { filterVisibilitySectionsByConfig, getMappedViewSettings } from './utils';

const disableProfileRecommendations = (settings: IViewVisibilitySetting[]): IViewVisibilitySetting[] => {
  const visibilityService = new LocalVisibilitySettingsService(settings);
  visibilityService.setSettingVisibility(
    false,
    StudentSectionSettingTypeCodes.profile,
    StudentSectionSettingTypeCodes.profileRecommendations,
  );
  return visibilityService.result;
};

const visibilitySettingsSelector = createSelector(
  [
    userSettingsSelector,
    adminSettingsSelector,
    (state: IRootState) => state.adminViewMode,
    adminViewModeSettingsSelector,
    (state: IRootState) => userFunctionalitySelector(state),
    configSelector,
  ],
  (visibilitySettings, adminSettings, adminViewMode, adminViewModeSettings, user, config): IViewVisibilitySetting[] => {
    const isAdminViewing = user.isAdmin && adminViewMode.isViewing;
    const isAdminViewingWithoutSettings = isAdminViewing && adminViewMode.viewType === 'student';

    const userSettings = isAdminViewing ? adminViewModeSettings : visibilitySettings;

    if (isAdminViewingWithoutSettings) {
      const defaultSettings = toggleViewSettings(defaultVisibilitySettings, true);
      const adminDefaultSettings = disableProfileRecommendations(defaultSettings);

      // Фильтруем секции согласно фича флагам в конфиге
      const filteredSettings = filterVisibilitySectionsByConfig(adminDefaultSettings, config);
      return filteredSettings;
    }

    if (
      userSettings.loading ||
      adminSettings.loading ||
      !userSettings.content?.setting?.visibilitySettings ||
      adminSettings.content.sections.length === 0
    ) {
      return [];
    }

    const defaultSettings = filterVisibilitySectionsByConfig(defaultVisibilitySettings, config);
    const mappedSettings = getMappedViewSettings(
      defaultSettings,
      userSettings.content.setting.visibilitySettings || [],
      adminSettings.content.sections,
    );

    const normalizedSettings = normalizeViewSettings(mappedSettings);
    return isAdminViewing ? disableProfileRecommendations(normalizedSettings) : normalizedSettings;
  },
);

export default visibilitySettingsSelector;
